import React, { useState, useEffect } from 'react';
import { Search, Package, Eye, Calendar, User, MapPin, Truck, Printer } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import apiService from '../../services/apiService';
import { formatDate, formatMoney } from '../../utils/formatters';
import './PickingPage.css';

const PickingPage = () => {
  const { loading, setLoading, showToast, activeWarehouse } = useApp();

  const [searchQuery, setSearchQuery] = useState('');
  const [pedidos, setPedidos] = useState([]);
  const [filteredPedidos, setFilteredPedidos] = useState([]);
  const [selectedPedidos, setSelectedPedidos] = useState([]);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showProductosModal, setShowProductosModal] = useState(false);

  useEffect(() => {
    if (activeWarehouse) {
      loadData();
    }
  }, [activeWarehouse]);

  useEffect(() => {
    filterPedidos();
  }, [pedidos, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Obtener pedidos en estado "Almacen" y "Picking" para el almacén activo
      const [resultAlmacen, resultPicking] = await Promise.all([
        apiService.getVentasPorEstadoYAlmacen('Almacen', activeWarehouse.id),
        apiService.getVentasPorEstadoYAlmacen('Picking', activeWarehouse.id)
      ]);

      const pedidosAlmacen = resultAlmacen.success ? resultAlmacen.data || [] : [];
      const pedidosPicking = resultPicking.success ? resultPicking.data || [] : [];

      // Combinar ambos arrays
      const pedidosCombinados = [...pedidosAlmacen, ...pedidosPicking];

      setPedidos(pedidosCombinados);

      if (!resultAlmacen.success && !resultPicking.success) {
        showToast('Error al cargar pedidos', 'error');
      }
    } catch (error) {
      console.error('Error al cargar pedidos:', error);
      showToast('Error al cargar pedidos de Almacén', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterPedidos = () => {
    let filtered = pedidos;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pedido => {
        return (
          pedido.id?.toString().includes(query) ||
          pedido.folio?.toLowerCase().includes(query) ||
          pedido.cliente_nombre?.toLowerCase().includes(query)
        );
      });
    }

    setFilteredPedidos(filtered);
  };

  const handleSelectPedido = (pedidoId) => {
    setSelectedPedidos(prev => {
      if (prev.includes(pedidoId)) {
        return prev.filter(id => id !== pedidoId);
      } else {
        return [...prev, pedidoId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPedidos.length === filteredPedidos.length) {
      setSelectedPedidos([]);
    } else {
      setSelectedPedidos(filteredPedidos.map(pedido => pedido.id));
    }
  };

  const isPedidoSelected = (pedidoId) => {
    return selectedPedidos.includes(pedidoId);
  };

  const areAllSelected = () => {
    return filteredPedidos.length > 0 && selectedPedidos.length === filteredPedidos.length;
  };

  const areSomeSelected = () => {
    return selectedPedidos.length > 0 && selectedPedidos.length < filteredPedidos.length;
  };

  const handleViewDetail = async (pedido) => {
    try {
      setLoading(true);

      // Obtener detalle completo del pedido
      const result = await apiService.getVenta(pedido.id);

      if (result.success) {
        setSelectedPedido(result.data);
        setShowDetailModal(true);
      } else {
        showToast('Error al cargar detalle del pedido', 'error');
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      showToast('Error al cargar detalle del pedido', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerProductosSeleccionados = () => {
    if (selectedPedidos.length === 0) {
      showToast('Seleccione al menos un pedido', 'warning');
      return;
    }
    setShowProductosModal(true);
  };

  const getConsolidadoSeleccionados = () => {
    const pedidosSeleccionados = pedidos.filter(p => selectedPedidos.includes(p.id));
    const productosMap = {};

    pedidosSeleccionados.forEach(pedido => {
      if (pedido.detalles && Array.isArray(pedido.detalles)) {
        pedido.detalles.forEach(detalle => {
          const key = detalle.producto_id;

          if (!productosMap[key]) {
            productosMap[key] = {
              producto_id: detalle.producto_id,
              producto_nombre: detalle.producto_nombre,
              cantidad_total: 0,
              pedidos_count: 0
            };
          }

          productosMap[key].cantidad_total += parseFloat(detalle.cantidad);
          productosMap[key].pedidos_count += 1;
        });
      }
    });

    return Object.values(productosMap);
  };

  const handleImprimirLista = async () => {
    try {
      setLoading(true);

      // Actualizar estado de todos los pedidos seleccionados a "Picking"
      let exitosos = 0;
      let fallidos = 0;

      for (const ventaId of selectedPedidos) {
        try {
          const result = await apiService.actualizarEstadoVenta(ventaId, 'Picking');
          if (result.success) {
            exitosos++;
          } else {
            fallidos++;
          }
        } catch (error) {
          console.error(`Error al actualizar pedido ${ventaId}:`, error);
          fallidos++;
        }
      }

      // Mostrar resultado de actualización
      if (exitosos > 0 && fallidos === 0) {
        showToast(`${exitosos} pedido(s) actualizado(s) a estado Picking`, 'success');
      } else if (exitosos > 0 && fallidos > 0) {
        showToast(`${exitosos} actualizado(s), ${fallidos} fallido(s)`, 'warning');
      } else {
        showToast('No se pudo actualizar ningún pedido', 'error');
        setLoading(false);
        return;
      }

      // Generar la lista para imprimir
      const productosConsolidados = getConsolidadoSeleccionados();
      const totalUnidades = productosConsolidados.reduce((sum, p) => sum + p.cantidad_total, 0);
      const fecha = new Date().toLocaleString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Lista de Picking - ${activeWarehouse?.nombre || 'Almacén'}</title>
          <style>
            @media print {
              @page { margin: 1cm; }
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #667eea;
              padding-bottom: 15px;
            }
            .header h1 {
              margin: 0;
              color: #374151;
              font-size: 24px;
            }
            .header p {
              margin: 5px 0;
              color: #6b7280;
              font-size: 14px;
            }
            .info-section {
              background: #f9fafb;
              padding: 15px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
              font-size: 14px;
            }
            .info-label {
              font-weight: 600;
              color: #374151;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            thead {
              background: #667eea;
              color: white;
            }
            th {
              padding: 12px;
              text-align: left;
              font-weight: 600;
              font-size: 13px;
            }
            tbody tr {
              border-bottom: 1px solid #e5e7eb;
            }
            tbody tr:nth-child(even) {
              background: #f9fafb;
            }
            td {
              padding: 12px;
              font-size: 13px;
              color: #374151;
            }
            .checkbox-col {
              width: 40px;
              text-align: center;
            }
            .checkbox {
              width: 18px;
              height: 18px;
              border: 2px solid #6b7280;
              display: inline-block;
            }
            .cantidad-col {
              font-weight: 600;
              color: #667eea;
            }
            tfoot {
              background: #f3f4f6;
              border-top: 3px solid #667eea;
              font-weight: 700;
            }
            tfoot td {
              padding: 15px 12px;
              font-size: 15px;
            }
            .total-label {
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Lista de Picking</h1>
            <p>${activeWarehouse?.nombre || 'Almacén'}</p>
          </div>

          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Fecha:</span>
              <span>${fecha}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Total de Pedidos:</span>
              <span>${selectedPedidos.length}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Total de Productos:</span>
              <span>${productosConsolidados.length}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Total de Unidades:</span>
              <span>${totalUnidades}</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th class="checkbox-col">✓</th>
                <th>Producto</th>
                <th>Pedidos</th>
                <th>Unidades</th>
              </tr>
            </thead>
            <tbody>
              ${productosConsolidados.map(producto => `
                <tr>
                  <td class="checkbox-col"><span class="checkbox"></span></td>
                  <td>${producto.producto_nombre}</td>
                  <td>${producto.pedidos_count}</td>
                  <td class="cantidad-col">${producto.cantidad_total}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2" class="total-label">TOTAL</td>
                <td>${productosConsolidados.length} producto(s)</td>
                <td class="cantidad-col">${totalUnidades} unidades</td>
              </tr>
            </tfoot>
          </table>
        </body>
        </html>
      `;

      // Abrir ventana de impresión
      const ventanaImpresion = window.open('', 'Imprimir Lista de Picking', 'width=800,height=600');
      if (ventanaImpresion) {
        ventanaImpresion.document.write(htmlContent);
        ventanaImpresion.document.close();
        ventanaImpresion.onload = function () {
          ventanaImpresion.focus();
          setTimeout(() => {
            ventanaImpresion.print();
          }, 250);
        };
      }

      // Recargar datos y cerrar modal
      await loadData();
      setShowProductosModal(false);
      setSelectedPedidos([]);

    } catch (error) {
      console.error('Error al imprimir lista:', error);
      showToast('Error al procesar la lista de picking', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderPedidos = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando datos...</p>
        </div>
      );
    }

    if (filteredPedidos.length === 0) {
      return (
        <div className="empty-state">
          <Package size={48} />
          <h3>No se encontraron pedidos</h3>
          <p>
            {searchQuery
              ? 'No hay pedidos que coincidan con la búsqueda'
              : activeWarehouse
                ? `No hay pedidos en estado Almacén o Picking para ${activeWarehouse.nombre}`
                : 'Seleccione un almacén desde el header'
            }
          </p>
        </div>
      );
    }

    return (
      <table className="picking-table">
        <thead>
          <tr>
            <th className="checkbox-column">
              <input
                type="checkbox"
                checked={areAllSelected()}
                ref={input => {
                  if (input) {
                    input.indeterminate = areSomeSelected();
                  }
                }}
                onChange={handleSelectAll}
                title={areAllSelected() ? 'Deseleccionar todos' : 'Seleccionar todos'}
              />
            </th>
            <th>Folio</th>
            <th className="icon-column">Entrega</th>
            <th>Fecha</th>
            <th>Cliente</th>
            <th>Cantidad</th>
            <th>Estado</th>
            <th data-type="actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredPedidos.map((pedido) => {
            // Calcular total de unidades
            const totalUnidades = pedido.detalles?.reduce((sum, detalle) => {
              return sum + parseFloat(detalle.cantidad || 0);
            }, 0) || 0;

            return (
              <tr key={pedido.id} className={isPedidoSelected(pedido.id) ? 'selected-row' : ''}>
                <td className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={isPedidoSelected(pedido.id)}
                    onChange={() => handleSelectPedido(pedido.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td>
                  <span className="folio-badge">{pedido.folio || `#${pedido.id}`}</span>
                </td>
                <td className="icon-column">
                  {pedido.tipo_entrega === 'domicilio' ? (
                    <Truck size={18} title="Domicilio" className="delivery-icon domicilio" />
                  ) : (
                    <Package size={18} title="Pickup" className="delivery-icon pickup" />
                  )}
                </td>
                <td>{formatDate(pedido.fecha_creacion)}</td>
                <td>{pedido.cliente_nombre || 'Cliente General'}</td>
                <td>
                  <div className="cantidad-info">
                    <span className="text-bold">{pedido.total_productos || 0} productos</span>
                    <span className="cantidad-highlight">({totalUnidades} unidades)</span>
                  </div>
                </td>
                <td>
                  {pedido.estado && pedido.estado !== 'Almacen' && (
                    <span className={`status-badge status-${pedido.estado.toLowerCase()}`}>
                      {pedido.estado}
                    </span>
                  )}
                </td>
                <td className="actions">
                  <button
                    className="action-btn-compact"
                    onClick={() => handleViewDetail(pedido)}
                    title="Ver detalle"
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="picking-page">
      {/* Alerta de almacén */}
      {!activeWarehouse && (
        <div className="warehouse-alert">
          <Package size={20} />
          <span>Seleccione un almacén desde el header para ver los pedidos</span>
        </div>
      )}

      {/* Barra de búsqueda */}
      <div className="picking-controls">
        <div className="search-container">
          <Input
            placeholder="Buscar por folio, cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search size={20} />}
            className="search-input"
          />
        </div>

        {selectedPedidos.length > 0 && (
          <div className="controls-actions">
            <Button
              onClick={handleVerProductosSeleccionados}
              disabled={loading}
            >

              Ver Productos ({selectedPedidos.length}) Pedido(s)
            </Button>
          </div>
        )}
      </div>

      {/* Contenido de pedidos */}
      <div className="picking-content">
        {renderPedidos()}
      </div>

      {/* Modal de detalle de pedido */}
      {showDetailModal && selectedPedido && (
        <Modal
          title={`Detalle de Pedido - ${selectedPedido.folio || `#${selectedPedido.id}`}`}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedPedido(null);
          }}
          size="large"
        >
          <div className="venta-detail-modal">
            {/* Información general */}
            <div className="detail-section">
              <h3>Información General</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Folio:</label>
                  <span>{selectedPedido.folio || `#${selectedPedido.id}`}</span>
                </div>
                <div className="detail-item">
                  <label>Fecha:</label>
                  <span>
                    <Calendar size={14} />
                    {formatDate(selectedPedido.fecha_creacion)}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Cliente:</label>
                  <span>
                    <User size={14} />
                    <div className="cliente-info-modal">
                      <span>{selectedPedido.cliente_nombre || 'Cliente General'}</span>
                      {selectedPedido.cliente_nombre_comercial && (
                        <span className="nombre-comercial-modal">{selectedPedido.cliente_nombre_comercial}</span>
                      )}
                    </div>
                  </span>
                </div>
                <div className="detail-item">
                  <label>Atendió:</label>
                  <span>{selectedPedido.usuario_nombre || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Tipo de Entrega:</label>
                  <span>
                    {selectedPedido.tipo_entrega === 'domicilio' ? (
                      <>
                        <Truck size={14} />
                        A Domicilio
                      </>
                    ) : (
                      <>
                        <Package size={14} />
                        Pasan por Él
                      </>
                    )}
                  </span>
                </div>
                {selectedPedido.tipo_entrega === 'domicilio' && selectedPedido.direccion_entrega && (
                  <div className="detail-item full-width">
                    <label>Dirección de Entrega:</label>
                    <span>
                      <MapPin size={14} />
                      {selectedPedido.direccion_entrega}
                    </span>
                  </div>
                )}
                {selectedPedido.estado && (
                  <div className="detail-item">
                    <label>Estado:</label>
                    <span className={`status-badge status-${selectedPedido.estado.toLowerCase()}`}>
                      {selectedPedido.estado}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Productos */}
            <div className="detail-section">
              <h3>Productos</h3>
              <table className="detail-products-table">
                <thead>
                  <tr>
                    <th>Cantidad</th>
                    <th>Producto</th>
                    <th>Precio Unitario</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPedido.detalles && selectedPedido.detalles.length > 0 ? (
                    selectedPedido.detalles.map((detalle, index) => (
                      <tr key={index}>
                        <td>{detalle.cantidad}</td>
                        <td>{detalle.producto_nombre}</td>
                        <td>{formatMoney(detalle.precio_unitario)}</td>
                        <td className="text-right">{formatMoney(detalle.subtotal)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        No hay productos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totales */}
            <div className="detail-section">
              <div className="detail-totals">
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>{formatMoney(selectedPedido.subtotal || 0)}</span>
                </div>
                {selectedPedido.descuento > 0 && (
                  <div className="total-row">
                    <span>Descuento:</span>
                    <span className="text-danger">-{formatMoney(selectedPedido.descuento)}</span>
                  </div>
                )}
                {selectedPedido.impuesto > 0 && (
                  <div className="total-row">
                    <span>Impuesto:</span>
                    <span>{formatMoney(selectedPedido.impuesto)}</span>
                  </div>
                )}
                <div className="total-row total-final">
                  <span>Total:</span>
                  <span>{formatMoney(selectedPedido.total || 0)}</span>
                </div>
              </div>
            </div>

            {/* Observaciones */}
            {selectedPedido.observaciones && (
              <div className="detail-section">
                <h3>Observaciones</h3>
                <p className="detail-observaciones">{selectedPedido.observaciones}</p>
              </div>
            )}

            {/* Acciones */}
            <div className="detail-actions">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedPedido(null);
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de productos seleccionados */}
      {showProductosModal && (
        <Modal
          title={`Productos de ${selectedPedidos.length} Pedido(s)`}
          onClose={() => setShowProductosModal(false)}
          size="large"
        >
          <div className="productos-modal">
            <div className="modal-tab-content">
              <div className="consolidado-container">
                {(() => {
                  const productosConsolidados = getConsolidadoSeleccionados();

                  if (productosConsolidados.length === 0) {
                    return (
                      <div className="empty-state">
                        <Package size={48} />
                        <p>No hay productos en los pedidos seleccionados</p>
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="consolidado-header">
                        <h3>Total de Productos: {productosConsolidados.length}</h3>
                        <p className="consolidado-subtitle">
                          Lista de productos de {selectedPedidos.length} pedido(s)
                        </p>
                      </div>

                      <table className="picking-table">
                        <thead>
                          <tr>
                            <th width="70%">Producto</th>
                            <th width="30%">Cantidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productosConsolidados.map((producto, index) => (
                            <tr key={index}>
                              <td>
                                <div className="producto-info">
                                  <Package size={16} />
                                  <span className="producto-nombre">{producto.producto_nombre}</span>
                                </div>
                              </td>
                              <td>
                                <div className="cantidad-info">
                                  <span className="text-bold">{producto.pedidos_count} pedido(s)</span>
                                  <span className="cantidad-highlight">({producto.cantidad_total} unidades)</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="total-row-table">
                            <td className="text-bold">TOTAL</td>
                            <td>
                              <div className="cantidad-info">
                                <span className="text-bold">{productosConsolidados.length} producto(s)</span>
                                <span className="total-unidades">
                                  ({productosConsolidados.reduce((sum, p) => sum + p.cantidad_total, 0)} unidades)
                                </span>
                              </div>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Botones de acción */}
            <div className="detail-actions">
              <Button
                onClick={handleImprimirLista}
              >

                Imprimir Lista
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowProductosModal(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PickingPage;
